import React, { useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import styles from './Select.module.scss';
import { m, AnimatePresence } from 'framer-motion';
import cls from 'classnames';
import { ChevronIcon } from '../../icons/chevron';

function SelectDropdownElement(props: SelectDropdownItem) {
  return <span className={styles.listItemContent}>{props.text}</span>;
}

function SelectDropdown({
  onSelect,
  onClose,
  items,
  selected,
  isSpaced,
  title,
}: SelectDropdownProps) {
  const outside = useClickOutside<HTMLUListElement>(onClose);
  const id = `select-${title}`;

  return (
    <m.aside
      initial={{ opacity: 0.4, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={cls(styles.dropdown, isSpaced && styles.spaced)}
    >
      <h2 className="small-title" id={id}>
        {title}
      </h2>
      <ul
        ref={outside}
        role="listbox"
        className={cls(styles.list, isSpaced && styles.spaced)}
        aria-describedby={id}
      >
        {items.map((item) => {
          const isSelectedItem = selected.id === item.id;
          return (
            <li
              onClick={() => onSelect(item)}
              className={cls(styles.listItem, {
                [styles.spaced]: isSpaced,
                [styles.selected]: isSelectedItem,
              })}
              key={item.id}
              role="option"
              aria-selected={isSelectedItem}
            >
              <SelectDropdownElement {...item} />
            </li>
          );
        })}
      </ul>
    </m.aside>
  );
}

export type SelectDropdownItem = {
  icon?: string;
  text: React.ReactNode;
  id: string;
};

type SelectDropdownProps = {
  title: string;
  items: SelectDropdownItem[];
  selected: SelectDropdownItem;
  required?: boolean;
  isSpaced?: boolean;
  onClose(): void;
  onSelect(item: SelectDropdownItem): void;
};

export function Select(props: SelectProps) {
  const { onSelect: emitSelect, isSpaced = false, title, value } = props;

  const [open, setOpen] = useState(false);

  const onClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const onSelect = React.useCallback(
    (item: SelectDropdownItem) => {
      setOpen(false);
      emitSelect?.(item);
    },
    [emitSelect],
  );

  return (
    <div className={styles.buttonWrapper}>
      <button
        type="button"
        className={cls(styles.button, open && styles.active)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-describedby={props.describedBy}
        onClick={() => setOpen(true)}
      >
        <SelectDropdownElement key={value.id} {...value} />
        <ChevronIcon className={cls(styles.dropdownIcon, open && styles.flipped)} fill="white" />
      </button>
      <AnimatePresence>
        {open && (
          <SelectDropdown
            title={title}
            isSpaced={isSpaced}
            onSelect={onSelect}
            onClose={onClose}
            items={props.items}
            selected={value}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export type SelectProps = {
  /**
   * The id of the labeling element.
   */
  describedBy?: string;
  value: SelectDropdownItem;
  required?: boolean;
  items: SelectDropdownItem[];
  defaultId: string;
  title: string;
  onSelect?(item: SelectDropdownItem): void;
  isSpaced?: boolean;
};
